import React, { useCallback, useContext, useMemo, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';

import AccountPickerContext from '../../../contexts/AccountPickerContext';
import V3ContentScreenContext from './V3ContentScreenContext';
import STORE from '../../../store';
import { contentTypeCheckers } from '../ContentIndex/useContent';
import Tooltip from '../../../components/Tooltip';
import Button from '../../../kingpin/atoms/Button';
import delay from '../../../delay';
import ToastContext from '../../../contexts/ToastContext';
import captureException from '../../../services/captureException';
import Typography from '../../../kingpin/atoms/Typography';
import isPerformanceBoard from '../../../isPerformanceBoard';
import Form from '../../../kingpin/forms/Form';
import FormHeader from '../../../kingpin/forms/FormHeader';
import FormContent from '../../../kingpin/forms/FormContent';
import Row from '../../../components/Common/Row';
import { useIsCurrentUserFleetOpsStaff } from '../../../components/Common/FleetOpsStaffOnly';
import FormInput from '../../../kingpin/forms/FormInput';
import Inputs from '../../../components/Inputs';
import usePopup from '../../../hooks/usePopup';

const TooltipContent = ({ contentLabel }: { contentLabel: string }) => (
  <div>
    <Typography.Body type={'Body 14 Bold'} color={'var(--color-tooltip-text)'}>
      {`Publishing makes the ${contentLabel}`}
    </Typography.Body>
    <ul style={{ margin: 0, padding: 0, paddingLeft: 24 }}>
      <li>
        <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
          Shareable with users.
        </Typography.Body>
      </li>
      <li>
        <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
          {"Visible in workspaces & apps where it's included"}
        </Typography.Body>
      </li>
      <li>
        <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
          Accessible to system admins
        </Typography.Body>
      </li>
    </ul>
  </div>
);

const LabelsConfirmation = ({
  content,
  contentLabel,
  onPublishConfirmed,
  close,
}: {
  content: ContentIndexTypes.ContentItem;
  contentLabel: string;
  onPublishConfirmed: (newLabelsIds: string[]) => Promise<void>;
  close: () => void;
}) => {
  const [newLabelIds, setNewLabelids] = useState<string[]>(() => {
    return content.labelIds;
  });

  return (
    <Form>
      <FormHeader title={`Publish ${contentLabel}`} onClose={close} />
      <FormContent>
        <FormInput label={'Labels'}>
          <Inputs.LabelsPicker
            labelIds={newLabelIds}
            setLabelIds={setNewLabelids}
          />
        </FormInput>
      </FormContent>
      <Row spaceBetween>
        <Button
          size={'Medium'}
          type={'Secondary'}
          label={'Cancel'}
          onClick={close}
        />
        <Button
          size={'Medium'}
          type={'Primary'}
          label={'Publish'}
          isDisabled={newLabelIds.length === 0}
          onClick={() => {
            if (newLabelIds.length === 0) {
              return;
            }
            onPublishConfirmed(newLabelIds);
          }}
        />
      </Row>
    </Form>
  );
};

const V3PublishButton = () => {
  const { isOpen, open, close } = usePopup();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { content } = useContext(V3ContentScreenContext);
  const { showToast } = useContext(ToastContext);
  const isFleetOpsStaff = useIsCurrentUserFleetOpsStaff();

  const updateDashboardToPublished = useCallback(
    (dashboard: DashboardType, labelIds: string[] | undefined) => {
      const newDashboard: DashboardType = {
        ...dashboard,
        isV3Published: true,
        labelIds: labelIds ? labelIds : dashboard.labelIds,
      };

      return STORE.getDashboardsRef({ accountId: selectedAccountId })
        .doc(newDashboard.id)
        .set(newDashboard);
    },
    [selectedAccountId],
  );
  const updateReportToPublished = useCallback(
    (report: ReportType, labelIds: string[] | undefined) => {
      const newReport: ReportType = {
        ...report,
        isV3Published: true,
        labelIds: labelIds ? labelIds : report.labelIds,
      };

      return STORE.getReportsRef({ accountId: selectedAccountId })
        .doc(newReport.id)
        .set(newReport);
    },
    [selectedAccountId],
  );
  const updateScorecardToPublished = useCallback(
    (scorecard: Scorecards.Scorecard, labelIds: string[] | undefined) => {
      const newScorecard: Scorecards.Scorecard = {
        ...scorecard,
        isV3Published: true,
        labelIds: labelIds ? labelIds : scorecard.labelIds,
      };

      return STORE.visualisations
        .getScorecardsRef({ accountId: selectedAccountId })
        .doc(newScorecard.id)
        .set(newScorecard);
    },
    [selectedAccountId],
  );
  const updateGoalToPublished = useCallback(
    (goal: GeneralGoal, labelIds: string[] | undefined) => {
      const newGoal: GeneralGoal = {
        ...goal,
        isV3Published: true,
        labelIds: labelIds ? labelIds : goal.labelIds,
      };

      return STORE.visualisations
        .getGoalsRef({ accountId: selectedAccountId })
        .doc(newGoal.id)
        .set(newGoal);
    },
    [selectedAccountId],
  );
  const updateBoardToPublished = useCallback(
    (
      board: Board | PerformanceBoardTypes.Board,
      labelIds: string[] | undefined,
    ) => {
      if (isPerformanceBoard(board)) {
        const newBoard: PerformanceBoardTypes.Board = {
          ...board,
          isV3Published: true,
          labelIds: labelIds ? labelIds : board.labelIds,
        };
        return STORE.contentDistributions
          .getPerformanceBoardsRef({ accountId: selectedAccountId })
          .doc(newBoard.id)
          .set(newBoard);
      }

      const newBoard: Board = {
        ...board,
        isV3Published: true,
        labelIds: labelIds ? labelIds : board.labelIds,
      };
      return STORE.getBoardsRef({ accountId: selectedAccountId })
        .doc(newBoard.id)
        .set(newBoard);
    },
    [selectedAccountId],
  );

  const contentLabel = useMemo(() => {
    if (!content) {
      return '';
    }

    if (contentTypeCheckers.isDashboard(content)) {
      return 'Dashboard';
    } else if (contentTypeCheckers.isReport(content)) {
      return 'Report';
    } else if (contentTypeCheckers.isGoal(content)) {
      return 'Goal';
    } else if (contentTypeCheckers.isScorecard(content)) {
      return 'KPI List';
    } else if (contentTypeCheckers.isBoard(content)) {
      return 'Board';
    } else {
      return '';
    }
  }, [content]);

  const updateContentToPublished = useCallback(
    (labelIds: string[] | undefined): Promise<void> => {
      if (!content) {
        const e = new Error();
        e.name = 'Content not defined';
        throw e;
      }

      if (contentTypeCheckers.isDashboard(content)) {
        return updateDashboardToPublished(content.dashboard, labelIds);
      } else if (contentTypeCheckers.isReport(content)) {
        return updateReportToPublished(content.report, labelIds);
      } else if (contentTypeCheckers.isGoal(content)) {
        return updateGoalToPublished(content.goal, labelIds);
      } else if (contentTypeCheckers.isScorecard(content)) {
        return updateScorecardToPublished(content.scorecard, labelIds);
      } else if (contentTypeCheckers.isBoard(content)) {
        return updateBoardToPublished(content.board, labelIds);
      } else {
        const e = new Error();
        e.name = 'Unexpected content type';
        throw e;
      }
    },
    [
      content,
      updateBoardToPublished,
      updateDashboardToPublished,
      updateGoalToPublished,
      updateReportToPublished,
      updateScorecardToPublished,
    ],
  );

  const onPublishConfirmed = useCallback(
    async (labelIds?: string[]) => {
      setIsSaving(true);
      await delay(1000);
      return updateContentToPublished(labelIds)
        .then(() => {
          setIsSaving(false);
          showToast(`${contentLabel} published`);
        })
        .catch((ex) => {
          setIsSaving(false);
          showToast(
            `We couldn't publish this ${contentLabel} due to a technical reason.`,
          );
          captureException(ex);
        });
    },
    [contentLabel, showToast, updateContentToPublished],
  );

  const onPublishClicked = useCallback(() => {
    if (isFleetOpsStaff) {
      open();
      return;
    }

    return onPublishConfirmed();
  }, [isFleetOpsStaff, onPublishConfirmed, open]);

  if (!content) {
    return null;
  }

  return (
    <>
      <Tooltip
        content={<TooltipContent contentLabel={contentLabel} />}
        position={'bottom-start'}
      >
        <Button
          onClick={onPublishClicked}
          type={isSaving ? 'Secondary' : 'Primary'}
          size="Small"
          label={isSaving ? 'Publishing' : `Publish ${contentLabel}`}
          isLoading={isSaving}
          testId={`publish-button`}
        />
      </Tooltip>
      <ModalTransition>
        {isOpen && (
          <Modal>
            <LabelsConfirmation
              close={close}
              content={content}
              contentLabel={contentLabel}
              onPublishConfirmed={onPublishConfirmed}
            />
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export default V3PublishButton;
